import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Briefcase, Gauge, PhoneIncoming, Activity, type LucideIcon } from "lucide-react"

import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card"
import { formatNumber } from "@/shared/lib/staffing-shared"

import { HeatmapGrid, HeatmapSkeleton } from "./HeatmapGrid"
import { DailySummaryTable, DailySummaryTableSkeleton } from "./DailySummaryTable"
import { HourlyBreakdownTable, HourlyBreakdownTableSkeleton } from "./HourlyBreakdownTable"
import type { WeeklyResult, WeeklyCalculatorStatus } from "../WeeklyCalculatorProvider"
import type { WeeklyCalculatorFormValues } from "../types/form"
import type { DailySummaryData } from "../types/daily-summary"
import type { HourlyBreakdownData } from "../types/hourly-breakdown"
import type { HeatmapData, HeatmapCell, WeekStartDay } from "../types/heatmap"
import type { IntervalData } from "../api"

type WeeklyResultsCardProps = {
  result: WeeklyResult | null
  status: WeeklyCalculatorStatus
  formValues: WeeklyCalculatorFormValues
}

type MetricCardProps = {
  label: string
  value: string
  helper: string
  icon: LucideIcon
}

const DAY_KEYS = [
  "form.fields.distribution.monday",
  "form.fields.distribution.tuesday",
  "form.fields.distribution.wednesday",
  "form.fields.distribution.thursday",
  "form.fields.distribution.friday",
  "form.fields.distribution.saturday",
  "form.fields.distribution.sunday",
] as const

// Map weekStart values to day index (Monday = 0, Sunday = 6)
const WEEK_START_INDEX: Record<string, number> = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6,
}

/**
 * Reorder array starting from a given index
 */
function reorderFromIndex<T>(arr: readonly T[], startIndex: number): T[] {
  return [...arr.slice(startIndex), ...arr.slice(0, startIndex)]
}

/**
 * Transform intervals into DailySummaryData format
 */
function transformToDailySummary(
  intervals: IntervalData[],
  totalWeeklyCalls: number,
  workingHoursPerWeek: number,
  intervalMinutes: number,
  weekStart: string,
): DailySummaryData {
  // Get day keys ordered by weekStart (backend already orders intervals by weekStart)
  const weekStartIndex = WEEK_START_INDEX[weekStart] ?? 0
  const orderedDayKeys = reorderFromIndex(DAY_KEYS, weekStartIndex)

  // Group intervals by day
  const dayGroups: Map<number, IntervalData[]> = new Map()
  for (const interval of intervals) {
    const existing = dayGroups.get(interval.dayIndex) || []
    existing.push(interval)
    dayGroups.set(interval.dayIndex, existing)
  }

  const rows = []
  let maxAgentsOverall = 0

  for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
    const dayIntervals = dayGroups.get(dayIndex) || []
    const dailyCalls = dayIntervals.reduce((sum, i) => sum + i.calls, 0)
    // Use actual max scheduledAgents (no rounding up)
    const maxAgents =
      dayIntervals.length > 0 ? Math.max(...dayIntervals.map((i) => i.scheduledAgents)) : 0

    // Calculate raw agent hours for this day
    const hoursPerInterval = intervalMinutes / 60
    const rawAgentHours = dayIntervals.reduce(
      (sum, i) => sum + i.scheduledAgents * hoursPerInterval,
      0,
    )

    // FTE Needed = raw agent hours / hours per FTE per day (5 working days)
    // This assumes 100% schedule fit
    const hoursPerFtePerDay = workingHoursPerWeek / 5
    const fteNeeded = rawAgentHours / hoursPerFtePerDay

    // People Hours includes scheduling overhead (reference uses 8.25 multiplier instead of 7.5)
    // This accounts for shift overlaps, breaks, and scheduling inefficiency
    const SCHEDULING_OVERHEAD_FACTOR = 1.1
    const dailyPeopleHours = fteNeeded * hoursPerFtePerDay * SCHEDULING_OVERHEAD_FACTOR

    // Typical FTE = FTE Needed × overhead factor (reference methodology)
    const typicalFte = Math.round(fteNeeded * SCHEDULING_OVERHEAD_FACTOR * 10) / 10

    // Call volume percentage
    const callVolumePercent =
      totalWeeklyCalls > 0 ? Math.round((dailyCalls / totalWeeklyCalls) * 100) : 0

    maxAgentsOverall = Math.max(maxAgentsOverall, maxAgents)

    // Generate a date for display (using current week as reference)
    // Calculate based on weekStart, not fixed Monday
    const today = new Date()
    const currentDayOfWeek = today.getDay() // 0 = Sunday, 1 = Monday, ...
    // Convert to our format (0 = Monday, 6 = Sunday)
    const currentDayIndex = (currentDayOfWeek + 6) % 7
    // Find distance from weekStart
    const daysFromWeekStart = (currentDayIndex - weekStartIndex + 7) % 7
    const weekStartDate = new Date(today)
    weekStartDate.setDate(today.getDate() - daysFromWeekStart)
    const targetDate = new Date(weekStartDate)
    targetDate.setDate(weekStartDate.getDate() + dayIndex)
    const dateStr = targetDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })

    rows.push({
      dayKey: orderedDayKeys[dayIndex],
      date: dateStr,
      callVolumePercent,
      calls: dailyCalls,
      maxAgentsNeeded: maxAgents,
      fteNeeded: parseFloat(fteNeeded.toFixed(1)),
      peopleHours: parseFloat(dailyPeopleHours.toFixed(1)),
      typicalFte,
    })
  }

  return { rows, maxAgentsOverall }
}

/**
 * Transform intervals into HourlyBreakdownData format
 */
function transformToHourlyBreakdown(intervals: IntervalData[]): HourlyBreakdownData {
  // Get unique time slots
  const timeSlotSet = new Set<string>()
  for (const interval of intervals) {
    timeSlotSet.add(interval.time)
  }
  const timeSlots = Array.from(timeSlotSet).sort()

  // Keep time slots in 24-hour format (e.g., "08:00", "13:00")
  const formattedTimeSlots = timeSlots.map((time) => {
    const [hours, minutes] = time.split(":").map(Number)
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
  })

  // Group by day
  const dayGroups: Map<number, IntervalData[]> = new Map()
  for (const interval of intervals) {
    const existing = dayGroups.get(interval.dayIndex) || []
    existing.push(interval)
    dayGroups.set(interval.dayIndex, existing)
  }

  // Build days array
  const days = []
  for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
    const dayIntervals = dayGroups.get(dayIndex) || []
    const totalCalls = dayIntervals.reduce((sum, i) => sum + i.calls, 0)

    // Map intervals to hours array (matching timeSlots order)
    const hours = timeSlots.map((time) => {
      const interval = dayIntervals.find((i) => i.time === time)
      return {
        calls: interval?.calls || 0,
        agents: interval?.scheduledAgents || 0,
      }
    })

    days.push({
      dayKey: DAY_KEYS[dayIndex],
      totalCalls,
      hours,
    })
  }

  return { timeSlots: formattedTimeSlots, days }
}

/**
 * Get operating start hour based on hours open per day.
 * For 24-hour operations, start at midnight (00:00).
 * For all other operations, start at 08:00.
 */
function getOperatingStartHour(hoursOpenPerDay: number): number {
  return hoursOpenPerDay === 24 ? 0 : 8
}

/**
 * Transform intervals into HeatmapData format
 * Uses reporting interval as bucket size and only shows operating hours
 */
function transformToHeatmapData(
  intervals: IntervalData[],
  hoursOpenPerDay: number,
  reportingInterval: string,
): HeatmapData {
  // Calculate interval minutes and number of buckets
  const intervalMinutes =
    reportingInterval === "15min" ? 15 : reportingInterval === "60min" ? 60 : 30
  const totalMinutes = hoursOpenPerDay * 60
  const numBuckets = Math.ceil(totalMinutes / intervalMinutes)
  const operatingStartHour = getOperatingStartHour(hoursOpenPerDay)

  // Initialize 7 days x numBuckets
  const bucketCalls: number[][] = Array.from({ length: 7 }, () =>
    Array.from({ length: numBuckets }, () => 0),
  )

  // Group intervals into buckets based on reporting interval
  for (const interval of intervals) {
    const [hourStr, minStr] = interval.time.split(":")
    const hour = parseInt(hourStr, 10)
    const minute = parseInt(minStr, 10)

    // Calculate minutes from operating start
    const minutesFromStart = (hour - operatingStartHour) * 60 + minute
    const bucketIndex = Math.floor(minutesFromStart / intervalMinutes)

    if (
      bucketIndex >= 0 &&
      bucketIndex < numBuckets &&
      interval.dayIndex >= 0 &&
      interval.dayIndex < 7
    ) {
      bucketCalls[interval.dayIndex][bucketIndex] += interval.calls
    }
  }

  // Find global maximum for color normalization
  let maxCalls = 0
  for (let day = 0; day < 7; day++) {
    for (let bucket = 0; bucket < numBuckets; bucket++) {
      maxCalls = Math.max(maxCalls, bucketCalls[day][bucket])
    }
  }

  // Generate time labels
  const timeLabels: string[] = []
  for (let i = 0; i < numBuckets; i++) {
    const totalMinutesFromStart = i * intervalMinutes
    const hour = operatingStartHour + Math.floor(totalMinutesFromStart / 60)
    const minute = totalMinutesFromStart % 60
    timeLabels.push(`${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`)
  }

  // Build cells array with actual call counts
  const cells: HeatmapCell[][] = []
  for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
    const dayRow: HeatmapCell[] = []
    for (let bucketIndex = 0; bucketIndex < numBuckets; bucketIndex++) {
      const calls = bucketCalls[dayIndex][bucketIndex]
      const totalMinutesFromStart = bucketIndex * intervalMinutes
      const hour = operatingStartHour + Math.floor(totalMinutesFromStart / 60)
      const minute = totalMinutesFromStart % 60

      dayRow.push({
        hour,
        minute,
        dayIndex,
        value: Math.round(calls),
      })
    }
    cells.push(dayRow)
  }

  return { cells, maxValue: maxCalls, timeLabels }
}

const MetricCard = ({ label, value, helper, icon: Icon }: MetricCardProps) => (
  <div className="relative stack-sm h-full rounded-xl bg-[var(--color-surface-muted)]/60 p-5 text-center overflow-visible">
    <Icon
      className="absolute -right-3 -bottom-3 size-20 text-[var(--color-border)]/50 pointer-events-none"
      strokeWidth={1.5}
      aria-hidden
    />
    <dt className="relative body-base font-medium text-[var(--color-text-secondary)]">{label}</dt>
    <dd className="relative heading-md text-[var(--color-text-primary)]">{value}</dd>
    <span className="relative body-xs text-[var(--color-text-secondary)]">{helper}</span>
  </div>
)

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h3 className="heading-sm text-[var(--color-text-primary)]">{children}</h3>
)

const ResultsSkeleton = () => {
  const { t } = useTranslation()

  return (
    <Card className="overflow-visible">
      <CardHeader className="border-b border-[var(--color-border)] bg-[var(--color-surface-muted)]/60">
        <CardTitle className="heading-sm text-[var(--color-text-primary)]">
          {t("weeklyResults.heading")}
        </CardTitle>
      </CardHeader>
      <CardContent className="stack-2xl pt-6">
        {/* Key Metrics */}
        <div className="stack-md animate-pulse">
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                className="space-y-3 rounded-xl bg-[var(--color-surface-muted)]/60 p-5"
                key={index}
              >
                <div className="h-3 w-1/2 mx-auto rounded bg-[var(--color-border)]/80" />
                <div className="h-6 w-3/4 mx-auto rounded bg-[var(--color-border)]/80" />
                <div className="h-3 w-2/3 mx-auto rounded bg-[var(--color-border)]/70" />
              </div>
            ))}
          </div>
          <div className="h-4 w-3/4 rounded bg-[var(--color-border)]/50" />
        </div>
        {/* Daily Summary */}
        <div className="stack-md">
          <SectionHeading>{t("weeklyResults.sections.dailySummary")}</SectionHeading>
          <DailySummaryTableSkeleton />
        </div>
        <div className="stack-md">
          <SectionHeading>{t("weeklyResults.sections.demandHeatmap")}</SectionHeading>
          <HeatmapSkeleton />
        </div>
        <div className="stack-md">
          <SectionHeading>{t("weeklyResults.sections.hourlyBreakdown")}</SectionHeading>
          <HourlyBreakdownTableSkeleton />
        </div>
      </CardContent>
    </Card>
  )
}

export function WeeklyResultsCard({ result, status, formValues }: WeeklyResultsCardProps) {
  const { t } = useTranslation()

  // Compute interval minutes from reporting interval
  const intervalMinutes = useMemo(() => {
    switch (formValues.reportingInterval) {
      case "15min":
        return 15
      case "60min":
        return 60
      default:
        return 30
    }
  }, [formValues.reportingInterval])

  // Transform intervals data for child components
  const dailySummaryData = useMemo(() => {
    if (!result?.intervals?.length) return undefined
    return transformToDailySummary(
      result.intervals,
      result.callsPerWeek,
      parseFloat(formValues.workingHoursPerWeek),
      intervalMinutes,
      formValues.weekStart,
    )
  }, [result, formValues.workingHoursPerWeek, intervalMinutes, formValues.weekStart])

  const hourlyBreakdownData = useMemo(() => {
    if (!result?.intervals?.length) return undefined
    return transformToHourlyBreakdown(result.intervals)
  }, [result])

  const heatmapData = useMemo(() => {
    if (!result?.intervals?.length) return undefined
    return transformToHeatmapData(
      result.intervals,
      parseFloat(formValues.hoursOpenPerDay),
      formValues.reportingInterval,
    )
  }, [result, formValues.hoursOpenPerDay, formValues.reportingInterval])

  if (status === "idle") {
    return null
  }

  if (status === "calculating") {
    return <ResultsSkeleton />
  }

  if (!result) {
    return null
  }

  const metrics: MetricCardProps[] = [
    {
      label: t("weeklyResults.metrics.agentsFte.label"),
      value: formatNumber(result.agentsFte, 1),
      helper: t("weeklyResults.metrics.agentsFte.helper"),
      icon: Briefcase,
    },
    {
      label: t("weeklyResults.metrics.agentsPer30Mins.label"),
      value: formatNumber(result.agentsPer30MinsMax, 1),
      helper: t("weeklyResults.metrics.agentsPer30Mins.helper"),
      icon: Gauge,
    },
    {
      label: t("weeklyResults.metrics.callsPer30Mins.label"),
      value: formatNumber(result.callsPer30MinsMax),
      helper: t("weeklyResults.metrics.callsPer30Mins.helper"),
      icon: PhoneIncoming,
    },
    {
      label: t("weeklyResults.metrics.callsPerWeek.label"),
      value: formatNumber(result.callsPerWeek),
      helper: t("weeklyResults.metrics.callsPerWeek.helper"),
      icon: Activity,
    },
  ]

  return (
    <Card className="overflow-visible animate-[fadeUp_0.45s_ease-out]">
      <CardHeader className="border-b border-[var(--color-border)] bg-[var(--color-surface-muted)]/60">
        <CardTitle id="results-heading" tabIndex={-1} className="heading-sm text-[var(--color-text-primary)]">
          {t("weeklyResults.heading")}
        </CardTitle>
      </CardHeader>
      <CardContent className="stack-2xl pt-6">
        {/* Summary with service level target and assumptions (values as pills) */}
        <p className="body-base text-[var(--color-text-secondary)]">
          {t("weeklyResults.summary.weekStarting")}{" "}
          <span className="inline-block whitespace-nowrap rounded-full bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[var(--color-text-primary)]">
            {t(`form.weekStartOptions.${formValues.weekStart}`)}
          </span>
          . {t("weeklyResults.summary.start")}{" "}
          <span className="inline-block whitespace-nowrap rounded-full bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[var(--color-text-primary)]">
            {formValues.requiredServiceLevel}%
          </span>{" "}
          {t("weeklyResults.summary.answeredWithin")}{" "}
          <span className="inline-block whitespace-nowrap rounded-full bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[var(--color-text-primary)]">
            {formValues.targetAnswerTimeSeconds} {t("common.seconds")}
          </span>
          , {t("weeklyResults.summary.assuming")}{" "}
          <span className="inline-block whitespace-nowrap rounded-full bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[var(--color-text-primary)]">
            {formValues.shrinkage}%
          </span>{" "}
          {t("weeklyResults.summary.shrinkage")}{" "}
          <span className="inline-block whitespace-nowrap rounded-full bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[var(--color-text-primary)]">
            {formValues.maxOccupancy}%
          </span>{" "}
          {t("weeklyResults.summary.maxOccupancy")}{" "}
          <span className="inline-block whitespace-nowrap rounded-full bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[var(--color-text-primary)]">
            {formValues.workingHoursPerWeek} {t("common.hours")}
          </span>{" "}
          {t("weeklyResults.summary.perWeek")}{" "}
          <span className="inline-block whitespace-nowrap rounded-full bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[var(--color-text-primary)]">
            {formValues.averagePatienceSeconds} {t("common.seconds")}
          </span>{" "}
          {t("weeklyResults.summary.end")}
        </p>

        {/* Key Metrics */}
        <dl className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
          {metrics.map((metric) => (
            <MetricCard key={metric.label} {...metric} />
          ))}
        </dl>

        {/* Daily Summary */}
        <div className="stack-md">
          <SectionHeading>{t("weeklyResults.sections.dailySummary")}</SectionHeading>
          <DailySummaryTable
            data={dailySummaryData}
            reportingInterval={formValues.reportingInterval}
          />
        </div>

        {/* Demand Heatmap - shows actual calculated demand distribution */}
        <div className="stack-md">
          <SectionHeading>{t("weeklyResults.sections.demandHeatmap")}</SectionHeading>
          <HeatmapGrid data={heatmapData} weekStart={formValues.weekStart as WeekStartDay} />
        </div>

        {/* Hourly Breakdown */}
        <div className="stack-md">
          <SectionHeading>{t("weeklyResults.sections.hourlyBreakdown")}</SectionHeading>
          <HourlyBreakdownTable data={hourlyBreakdownData} />
        </div>
      </CardContent>
    </Card>
  )
}
